import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import SEOHead from '../../components/SEOHead';
import { calculateBorrowingPower, formatCurrency } from '../../utils/financeCalc';

export default function TruckFinanceCalculator() {
  const [monthlyIncome, setMonthlyIncome] = useState(9200);
  const [monthlyExpenses, setMonthlyExpenses] = useState(3600);
  const [existingDebt, setExistingDebt] = useState(700);
  const [assessmentRate, setAssessmentRate] = useState(9.5);
  const [termYears, setTermYears] = useState(5);

  const monthlySurplus = useMemo(() => Math.max(0, Number(monthlyIncome || 0) - Number(monthlyExpenses || 0) - Number(existingDebt || 0)), [monthlyIncome, monthlyExpenses, existingDebt]);
  const borrowingPower = useMemo(() => calculateBorrowingPower({ monthlySurplus, annualRate: Number(assessmentRate || 0), termYears: Number(termYears || 1) }), [monthlySurplus, assessmentRate, termYears]);

  return (
    <>
      <SEOHead
        title="Borrowing Power Calculator | Flash Funding"
        description="Estimate borrowing power for consumer and commercial applicants based on income, expenses, and debt commitments."
        canonical="/truck-finance-calculator"
      />

      <section className="py-16 sm:py-20" data-testid="borrowing-power-calculator-page">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-purple-200 bg-white p-6 sm:p-8" data-testid="borrowing-power-input-card">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-purple-100 px-3 py-1 text-xs text-purple-700" data-testid="borrowing-power-icon-pill"><BarChart3 size={14} /> Calculator</div>
            <h1 className="mb-3 text-3xl font-heading text-[#0F0A1A] sm:text-4xl" data-testid="borrowing-power-heading">Borrowing Power Calculator</h1>
            <p className="mb-3 text-sm text-[#4A4556]" data-testid="borrowing-power-subheading">Estimate borrowing range, then move to the right finance pathway.</p>
            <p className="mb-6 text-xs text-[#6C657E]" data-testid="borrowing-power-route-priority-text">
              Start with /car-loans, /personal-loans, /commercial-car-loans, or /business-finance. Use /apply when ready.
            </p>

            <div className="space-y-4">
              <div>
                <Label className="text-[#2E2742]">Net Monthly Income (AUD)</Label>
                <Input type="number" value={monthlyIncome} onChange={(e) => setMonthlyIncome(e.target.value)} className="mt-2 h-11 border-purple-200 bg-white text-[#1B1431]" data-testid="borrowing-income-input" />
              </div>
              <div>
                <Label className="text-[#2E2742]">Monthly Living Expenses (AUD)</Label>
                <Input type="number" value={monthlyExpenses} onChange={(e) => setMonthlyExpenses(e.target.value)} className="mt-2 h-11 border-purple-200 bg-white text-[#1B1431]" data-testid="borrowing-expenses-input" />
              </div>
              <div>
                <Label className="text-[#2E2742]">Existing Monthly Debt Repayments (AUD)</Label>
                <Input type="number" value={existingDebt} onChange={(e) => setExistingDebt(e.target.value)} className="mt-2 h-11 border-purple-200 bg-white text-[#1B1431]" data-testid="borrowing-existing-debt-input" />
              </div>
              <div>
                <Label className="text-[#2E2742]">Assessment Interest Rate (p.a.)</Label>
                <Input type="number" value={assessmentRate} onChange={(e) => setAssessmentRate(e.target.value)} className="mt-2 h-11 border-purple-200 bg-white text-[#1B1431]" data-testid="borrowing-rate-input" />
              </div>
              <div>
                <Label className="text-[#2E2742]">Term (Years)</Label>
                <Input type="number" value={termYears} onChange={(e) => setTermYears(e.target.value)} className="mt-2 h-11 border-purple-200 bg-white text-[#1B1431]" data-testid="borrowing-term-input" />
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-purple-200 bg-white p-6 sm:p-8" data-testid="borrowing-power-result-card">
            <h2 className="mb-6 text-xl font-semibold text-[#1B1431]">Estimated Position</h2>
            <div className="space-y-4 text-sm">
              <div className="flex justify-between border-b border-purple-100 pb-3">
                <span className="text-[#5C5671]">Monthly Surplus</span>
                <span className="font-mono text-[#1B1431]" data-testid="borrowing-monthly-surplus">{formatCurrency(monthlySurplus)}</span>
              </div>
              <div className="flex justify-between border-b border-purple-100 pb-3">
                <span className="text-[#5C5671]">Estimated Borrowing Power</span>
                <span className="font-mono text-purple-700" data-testid="borrowing-power-value">{formatCurrency(borrowingPower)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#5C5671]">Assessment Term</span>
                <span className="font-mono text-[#1B1431]" data-testid="borrowing-term-value">{termYears} years</span>
              </div>
            </div>

            <div className="mt-8 rounded-xl border border-purple-200 bg-[#f8f5fc] p-4">
              <p className="text-xs text-[#5C5671]" data-testid="borrowing-disclaimer">This is an indicative estimate only. Final assessment depends on full credit and policy checks.</p>
            </div>

            <div className="mt-8">
              <Link to="/personal-loans" data-testid="borrowing-calculator-apply-link">
                <Button className="w-full rounded-full bg-purple-600 text-xs uppercase tracking-[0.14em] text-white hover:bg-purple-700" data-testid="borrowing-calculator-apply-button">
                  See What You Can Get Approved For <ArrowRight size={14} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
